import type { RunStatus, RunTaskDetail, TaskRunStatus } from '@/entities/jobs';
import { formatTimeInterval } from '@/shared/lib/utils/string';
import type { TimeIntervalFormat } from '@/shared/lib/utils/string';
import type { StatusBadgeVariant } from './types';
import { TFunction } from 'i18next';

// Варианты значков статусов запуска
export const STATUS_VARIANTS: Record<RunStatus, StatusBadgeVariant> = {
  PENDING: 'secondary',
  RUNNING: 'primary',
  SUCCESS: 'primary',
  FAILED: 'destructive',
  CANCELLED: 'secondary',
};

// Варианты значков статусов задачи запуска
export const TASK_STATUS_VARIANTS: Record<TaskRunStatus, StatusBadgeVariant> = {
  PENDING: 'secondary',
  RUNNING: 'primary',
  SUCCESS: 'primary',
  FAILED: 'destructive',
  SKIPPED: 'secondary',
};

// Статусы, при которых запуск можно перезапустить
export const RESTARTABLE_STATUSES: RunStatus[] = ['SUCCESS', 'FAILED', 'CANCELLED'];

// Формирование markdown-отчёта по задаче для копирования
export const buildTaskMarkdownReport = (
  task: RunTaskDetail,
  runId: string,
  t: TFunction<['runs']>,
  timeDisplayFormat: TimeIntervalFormat,
): string => {
  const lines: string[] = [];

  lines.push(`# ${t('runs:taskDrawer.title')}`);
  lines.push('');
  lines.push(`\`${runId}\` / \`${task.id}\``);
  lines.push('');
  lines.push(`**${t('runs:taskDrawer.pipeline')}:** ${task.pipelineId}`);
  lines.push(`**${t('runs:taskDrawer.taskId')}:** ${task.taskId}`);
  lines.push(`**${t('runs:taskDrawer.status')}:** ${t(`runs:taskStatus.${task.status}`)}`);
  lines.push(`**${t('runs:taskDrawer.attempt')}:** ${task.attempt}`);
  if (task.duration !== null)
    lines.push(`**${t('runs:taskDrawer.duration')}:** ${formatTimeInterval(task.duration, timeDisplayFormat)}`);
  if (task.progress !== null) lines.push(`**${t('runs:taskDrawer.progress')}:** ${task.progress}%`);

  if (task.error) {
    lines.push('');
    lines.push('---');
    lines.push('');
    lines.push(`## ${t('runs:taskDrawer.error')}`);
    lines.push('');
    lines.push('```');
    lines.push(`${task.error.errorType}: ${task.error.message}`);
    lines.push('```');
    lines.push('');
    lines.push(
      `${t('runs:taskDrawer.willRetry')}: ${task.error.willRetry ? t('runs:taskDrawer.yes') : t('runs:taskDrawer.no')}`,
    );
  }

  const paramsJson = task.params && Object.keys(task.params).length > 0 ? JSON.stringify(task.params, null, 2) : null;
  if (paramsJson) {
    lines.push('');
    lines.push('---');
    lines.push('');
    lines.push(`## ${t('runs:taskDrawer.inputData')}`);
    lines.push('');
    lines.push('```json');
    lines.push(paramsJson);
    lines.push('```');
  }

  const resultsJson =
    task.results && Object.keys(task.results).length > 0 ? JSON.stringify(task.results, null, 2) : null;
  if (resultsJson) {
    lines.push('');
    lines.push('---');
    lines.push('');
    lines.push(`## ${t('runs:taskDrawer.outputData')}`);
    lines.push('');
    lines.push('```json');
    lines.push(resultsJson);
    lines.push('```');
  }

  return lines.join('\n');
};

// Базовый CSS-класс кружка статуса
export const CIRCLE_CLASS = 'flex shrink-0 items-center justify-center size-5 rounded-full border-1';

// Стили кружков статуса задач
type CircleStyle = { bg: string; border: string; icon: string };

export const TASK_CIRCLE_STYLES: Record<TaskRunStatus, CircleStyle> = {
  PENDING: {
    bg: 'bg-muted',
    border: 'border-border',
    icon: 'text-muted-foreground',
  },
  RUNNING: {
    bg: 'bg-warning',
    border: 'border-warning-border',
    icon: 'text-warning-foreground',
  },
  SUCCESS: {
    bg: 'bg-success',
    border: 'border-success-border',
    icon: 'text-success-foreground',
  },
  FAILED: {
    bg: 'bg-destructive',
    border: 'border-destructive-border',
    icon: 'text-destructive-foreground',
  },
  SKIPPED: {
    bg: 'bg-muted-foreground/30',
    border: 'border-muted-foreground/20',
    icon: 'text-muted-foreground',
  },
};

// Стили финального кружка (результат пайплайна)
export const RUN_CIRCLE_STYLES: Record<RunStatus, CircleStyle> = {
  PENDING: {
    bg: 'bg-muted',
    border: 'border-border',
    icon: 'text-muted-foreground',
  },
  RUNNING: {
    bg: 'bg-warning',
    border: 'border-warning-border',
    icon: 'text-warning-foreground',
  },
  SUCCESS: {
    bg: 'bg-success',
    border: 'border-success-border',
    icon: 'text-success-foreground',
  },
  FAILED: {
    bg: 'bg-destructive',
    border: 'border-destructive-border',
    icon: 'text-destructive-foreground',
  },
  CANCELLED: {
    bg: 'bg-muted-foreground/30',
    border: 'border-muted-foreground/20',
    icon: 'text-muted-foreground',
  },
};
